import formatDate from 'date-fns/format';
import React, {useCallback, useEffect, useLayoutEffect, useMemo, useRef} from 'react';
import {ScrollView, View} from 'react-native';
import {useSelector} from 'react-redux';
import {Route, useNavigation} from '@react-navigation/native';
import Screen from '../../shared/components/screen';
import {BreadcrumbItem} from '../../shared/components/breadcrumbs/model';
import {TransactionItem} from '../../shared/models/api/eCertificatesResponsePayload';
import {Routes, Screens} from '../../shared/routing';
import {CustomScrollView} from '../../shared/components/scrollView';
import {LoadableContent} from '../../shared/components/loadableContent';
import {Breadcrumbs} from '../../shared/components/breadcrumbs';
import {Tabs, TabsProps} from '../../shared/components/tabs';
import {useIsDesktop} from '../../shared/hooks/useIsDesktop';
import {useScrollViewMap} from '../../shared/components/yaMap/mobile/useScrollViewMap';
import {getCertificateInfoById, getCertificateStatementExport, getSellers} from '../../redux/actions/eCertificates';
import {useAppDispatch} from '../../redux';
import {certificateInfoSelector} from '../../redux/selectors/eCertificates';
import {InfoTab, InfoTabMobile, HistoryTab} from './components';
import {useCertificateItemStyles} from './CertificateItem.style';
import {isAmountChangeNegative} from './helpers';

const breadcrumbs: BreadcrumbItem[] = [
  {
    title: 'Возможности карты',
    routeName: Screens.CardServices,
    webRoute: Routes.cardService,
  },
  {
    title: 'Электронные сертификаты',
    routeName: Screens.ECertificates,
    webRoute: Routes.eCertificates,
  },
];

export function CertificateItemScreen({route}: {route: Route<string, {id: string}>}) {
  const id = route.params?.id;
  const styles = useCertificateItemStyles();
  const navigation = useNavigation();
  const isDesktop = useIsDesktop();
  const dispatch = useAppDispatch();
  const scrollViewRef = useRef<ScrollView>(null);
  const {scrollViewProps, mapProps} = useScrollViewMap();
  const certificateInfo = useSelector(certificateInfoSelector());

  const routes = useMemo(() => {
    if (certificateInfo) {
      return [
        ...breadcrumbs,
        {title: certificateInfo.templateName, routeName: Screens.CertificateItem, webRoute: Routes.certificateItem},
      ];
    }

    return breadcrumbs;
  }, [certificateInfo, id]);

  const transactions = useMemo(() => {
    if (!certificateInfo) return;

    const transactionsMap: Record<string, {amountChange: number; items: TransactionItem[]}> = {};
    const format = (date: string) => formatDate(new Date(date), 'yyyy-MM-dd');

    if (certificateInfo.transactions) {
      certificateInfo.transactions.forEach((item) => {
        const date = format(item.dateTime);

        if (!transactionsMap[date]) {
          transactionsMap[date] = {amountChange: 0, items: []};
        }

        const group = transactionsMap[date];
        const amountChange = certificateInfo.amountHistories?.find((i) => format(i.date) === date)?.amountChange;
        const k = isAmountChangeNegative(item.typeName) ? -1 : 1;

        group.amountChange = amountChange ?? group.amountChange + item.amount * k;
        group.items.push(item);
      });
    }

    if (certificateInfo.amountHistories) {
      certificateInfo.amountHistories.forEach((item) => {
        const date = format(item.date);

        if (!transactionsMap[date]) {
          transactionsMap[date] = {amountChange: item.amountChange, items: []};
        }
      });
    }

    return Object.entries(transactionsMap).sort(([date1], [date2]) => Date.parse(date2) - Date.parse(date1));
  }, [certificateInfo]);

  const handleExportPress = useCallback(() => {
    dispatch(getCertificateStatementExport(id));
  }, [id]);

  const tabsItems = useMemo<TabsProps['items']>(
    () => [
      {
        key: 'info',
        label: 'Сведения',
        isEmpty: !certificateInfo,
        children: isDesktop ? (
          <InfoTab certificate={certificateInfo} onExportCertificate={handleExportPress} />
        ) : (
          <InfoTabMobile mapProps={mapProps} certificate={certificateInfo} onExportCertificate={handleExportPress} />
        ),
      },
      {
        key: 'history',
        label: 'История',
        isEmpty: !(certificateInfo?.transactions && certificateInfo.transactions.length),
        children: <HistoryTab transactions={transactions} />,
      },
    ],
    [certificateInfo, mapProps, transactions],
  );

  useEffect(() => {
    if (id) {
      dispatch(getCertificateInfoById(id));
    }
  }, [id]);

  useEffect(() => {
    dispatch(getSellers());
  }, []);

  useLayoutEffect(() => {
    if (!certificateInfo) return;

    navigation.setOptions({
      title: certificateInfo.templateName?.trim() || 'Сертификат',
    });
  }, [certificateInfo, navigation]);

  return (
    <Screen>
      <CustomScrollView
        ref={scrollViewRef}
        contentInsetAdjustmentBehavior='automatic'
        onRefresh={() => {
          if (id) {
            dispatch(getCertificateInfoById(id));
          }
        }}
        {...scrollViewProps}>
        <LoadableContent loading={!certificateInfo}>
          <View style={styles.sectionContainer}>
            <Breadcrumbs routes={routes} />

            <View style={styles.tabsContainer}>
              <Tabs items={tabsItems} underlineActiveTab />
            </View>
          </View>
        </LoadableContent>
      </CustomScrollView>
    </Screen>
  );
}
